// Rest-timer alert sounds — 5 audibly distinct, fully synthesized tones (no audio files).
// One sound per concurrent training session so 5 simultaneous rest timers are
// distinguishable in the gym. The backend assigns each active session a slot (0-4);
// synthesizeTimerSound(ctx, index) returns the matching AudioBuffer.

export const TIMER_SOUND_COUNT = 5;

// Human-friendly names, keyed by index. Used for the on-screen "your sound" label.
export const TIMER_SOUND_NAMES = {
  es: ['Campana', 'Carillón', 'Pitido', 'Marimba', 'Din-Don'],
  en: ['Bell', 'Chime', 'Beep', 'Marimba', 'Ding-Dong'],
};

export function getTimerSoundName(index, language = 'es') {
  const names = TIMER_SOUND_NAMES[language] || TIMER_SOUND_NAMES.es;
  return names[((index % TIMER_SOUND_COUNT) + TIMER_SOUND_COUNT) % TIMER_SOUND_COUNT];
}

// Additive synthesis helper. `partials` = [{ freq, amp }].
// `envelope(t)` returns 0..1 gain at time t.
function renderTone(channel, sampleRate, startSec, durSec, partials, envelope, master) {
  const startIdx = Math.floor(startSec * sampleRate);
  const numSamples = Math.floor(durSec * sampleRate);
  for (let i = 0; i < numSamples; i++) {
    const t = i / sampleRate;
    let sample = 0;
    for (const p of partials) {
      sample += p.amp * Math.sin(2 * Math.PI * p.freq * t);
    }
    const idx = startIdx + i;
    if (idx < channel.length) {
      channel[idx] += sample * envelope(t) * master;
    }
  }
}

export function synthesizeTimerSound(ctx, index) {
  const i = ((index % TIMER_SOUND_COUNT) + TIMER_SOUND_COUNT) % TIMER_SOUND_COUNT;
  const sampleRate = ctx.sampleRate;
  const duration = 1.5;
  const buffer = ctx.createBuffer(1, Math.ceil(sampleRate * duration), sampleRate);
  const ch = buffer.getChannelData(0);

  switch (i) {
    case 0: {
      // Warm Bell — original tone (880Hz + harmonics, long exponential decay)
      renderTone(ch, sampleRate, 0, 1.5,
        [{ freq: 880, amp: 1 }, { freq: 1760, amp: 0.5 }, { freq: 2640, amp: 0.25 }],
        (t) => Math.exp(-t * 3), 0.4);
      break;
    }
    case 1: {
      // Ascending Chime — two rising notes (E5 -> B5)
      renderTone(ch, sampleRate, 0.0, 0.5,
        [{ freq: 659, amp: 1 }, { freq: 1318, amp: 0.3 }],
        (t) => Math.exp(-t * 6), 0.45);
      renderTone(ch, sampleRate, 0.28, 0.9,
        [{ freq: 988, amp: 1 }, { freq: 1976, amp: 0.3 }],
        (t) => Math.exp(-t * 4), 0.45);
      break;
    }
    case 2: {
      // Double Beep — sharp electronic double pip (bright, square-ish)
      const beep = [
        { freq: 1245, amp: 1 }, { freq: 2490, amp: 0.35 }, { freq: 3735, amp: 0.18 },
      ];
      const pip = (t) => {
        const attack = Math.min(1, t / 0.008);
        return attack * Math.exp(-t * 22);
      };
      renderTone(ch, sampleRate, 0.0, 0.18, beep, pip, 0.4);
      renderTone(ch, sampleRate, 0.22, 0.18, beep, pip, 0.4);
      break;
    }
    case 3: {
      // Woody Marimba — short percussive wooden knock (C5, fast decay)
      renderTone(ch, sampleRate, 0, 0.8,
        [{ freq: 523, amp: 1 }, { freq: 1046, amp: 0.3 }, { freq: 1569, amp: 0.12 }],
        (t) => Math.exp(-t * 9), 0.55);
      break;
    }
    case 4: {
      // Ding-Dong — descending doorbell (G5 -> D5)
      renderTone(ch, sampleRate, 0.0, 0.5,
        [{ freq: 784, amp: 1 }, { freq: 1568, amp: 0.25 }],
        (t) => Math.exp(-t * 5), 0.5);
      renderTone(ch, sampleRate, 0.4, 1.0,
        [{ freq: 587, amp: 1 }, { freq: 1174, amp: 0.25 }],
        (t) => Math.exp(-t * 4), 0.5);
      break;
    }
    default:
      break;
  }
  return buffer;
}
